<template>
  <div class="sso-wrap">
    <div class="sso-glow sso-glow-a"></div>
    <div class="sso-glow sso-glow-b"></div>
    <div class="sso-grid"></div>

    <div class="sso-card">
      <!-- 品牌区 -->
      <div class="sso-brand">
        <div class="sso-logo">焱</div>
        <div class="sso-brand-text">
          <div class="sso-brand-name">焱火体育 · OA 办公</div>
          <div class="sso-brand-sub">管理端 · 考勤 / 审批 / 表单</div>
        </div>
        <div class="sso-badge">免登中</div>
      </div>

      <!-- 状态区 -->
      <div class="sso-body">
        <!-- 成功 -->
        <template v-if="state === 'success'">
          <div class="sso-icon is-ok">
            <svg viewBox="0 0 52 52" class="sso-svg">
              <circle cx="26" cy="26" r="24" fill="none" stroke="currentColor" stroke-width="3"/>
              <path d="M15 27 l8 8 l14 -15" fill="none" stroke="currentColor" stroke-width="4"
                    stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="sso-title">登录成功</div>
          <div class="sso-desc">正在进入 OA 系统，请稍候…</div>
        </template>

        <!-- 失败 -->
        <template v-else-if="state === 'error'">
          <div class="sso-icon is-err">
            <svg viewBox="0 0 52 52" class="sso-svg">
              <circle cx="26" cy="26" r="24" fill="none" stroke="currentColor" stroke-width="3"/>
              <path d="M26 14 L26 30" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
              <circle cx="26" cy="37" r="2.6" fill="currentColor"/>
            </svg>
          </div>
          <div class="sso-title">免登失败</div>
          <div class="sso-desc is-err-text">{{ errMsg }}</div>
          <div class="sso-actions">
            <button class="sso-btn is-primary" @click="backToAdmin">返回管理后台</button>
            <button class="sso-btn" @click="toLoginPage">用账号密码登录</button>
          </div>
        </template>

        <!-- 处理中 -->
        <template v-else>
          <div class="sso-spinner">
            <span></span><span></span><span></span>
          </div>
          <div class="sso-title">{{ stepText }}</div>
          <div class="sso-desc">请稍候，正在校验免登票据…</div>
          <div class="sso-progress"><i :style="{ width: progress + '%' }"></i></div>
        </template>
      </div>

      <!-- 步骤条（仅处理中显示，避免失败时信息过载） -->
      <div v-if="state === 'loading'" class="sso-steps">
        <div class="sso-step" :class="{ 'is-done': progress > 20 }">
          <i>1</i><span>校验免登票据</span>
        </div>
        <div class="sso-step-line"></div>
        <div class="sso-step" :class="{ 'is-done': progress >= 45 }">
          <i>2</i><span>换取登录态</span>
        </div>
        <div class="sso-step-line"></div>
        <div class="sso-step" :class="{ 'is-done': progress >= 100 }">
          <i>3</i><span>进入系统</span>
        </div>
      </div>

      <div class="sso-foot">
        <span class="sso-dot"></span>
        焱火体育 · 统一身份安全登录 &nbsp;|&nbsp; 票据 120 秒内有效，仅可使用一次
      </div>
    </div>
  </div>
</template>

<script>
import {ssoLogin} from "@/api/sso";

/**
 * 跨系统免登落地页 —— OA 管理端（workflow-ui · oa.yanhuotiyu.com）
 *
 * 入口：焱火后台管理系统顶部「OA办公」→ 后端签发一次性签名票据 →
 *      浏览器打开本页（/#/ssoLogin?phone=..&ts=..&nonce=..&sign=..）→ 本页换票 → 进入 OA。
 *
 * ⚠️ 三个关键点（改动前务必读懂）：
 *  1. 换票前必须清掉本地旧登录态。RequestDetailFilter 的判定是「只要请求带了 token + userId
 *     且 referer 可识别，就必须通过 Redis 校验」，白名单只在**无 token** 时才生效；
 *     旧 token 若已过期，带它会直接被 401「登陆失效」。
 *  2. OA 同一套后端服务两个前端：本管理端（oa.xxx / 本地 88 → type=web）与
 *     m 站表单端（oam.xxx / 本地 89 → type=app）。服务端换票会同时写 web / app 两个通道，
 *     所以从后台进来一次，两端都不需要再登录。
 *  3. 落地数据结构必须与账号密码登录完全一致（systemIndex + systemList + userType=pc）。
 */
export default {
  name: "SsoLogin",
  data() {
    return {
      state: "loading", // loading | success | error
      stepText: "正在校验免登票据",
      progress: 20,
      errMsg: ""
    };
  },
  mounted() {
    this.handleSso();
  },
  methods: {
    handleSso() {
      // ① 清掉可能已失效的旧登录态，避免被过滤器 401 拦掉（详见文件头第 1 条）
      localStorage.removeItem("user");

      // hash 模式下参数位于 #/ssoLogin?phone=..&ts=..&nonce=..&sign=..
      const q = this.$route.query || {};
      if (!q.phone || !q.ts || !q.nonce || !q.sign) {
        this.fail("免登参数不完整，请从焱火后台管理系统顶部的「OA办公」入口进入。");
        return;
      }

      this.state = "loading";
      this.stepText = "正在校验免登票据";
      this.progress = 45;

      ssoLogin({
        phone: q.phone,
        ts: q.ts,
        nonce: q.nonce,
        sign: q.sign,
        from: q.from || "sport-admin",
        clientType: q.clientType || "web"
      })
        .then(res => {
          if (res.status === 200 && res.data && res.data.code == 200 && res.data.data) {
            this.stepText = "登录成功，正在进入系统";
            this.progress = 100;
            this.state = "success";
            this.enter(res.data.data);
          } else {
            this.fail((res.data && res.data.message) || "免登失败，请返回管理后台重试。");
          }
        })
        .catch(err => {
          const status = err && err.response && err.response.status;
          if (status === 401) {
            this.fail("登录态已失效，请返回管理后台重新进入。");
          } else {
            this.fail("网络异常，请检查网络后返回管理后台重试。");
          }
        });
    },

    /**
     * 落地：与账号密码登录保持完全一致的数据结构，避免后续页面取值差异
     */
    enter(user) {
      user.systemIndex = 0;
      user.systemList = user.menuVo;
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("userType", "pc");
      localStorage.setItem("router-path", "/admin/home");

      const q = this.$route.query || {};
      const redirect = q.redirect && String(q.redirect).indexOf("/") === 0
        ? String(q.redirect)
        : "/admin/home";

      // 留一点时间让成功态可见，避免「闪一下就没」
      setTimeout(() => {
        this.$router.replace(redirect);
      }, 350);
    },

    fail(msg) {
      this.state = "error";
      this.errMsg = msg;
      this.progress = 100;
    },

    backToAdmin() {
      // 生产环境返回焱火后台管理系统；本地联调返回本地后台端口
      const isProd = window.location.hostname.indexOf("yanhuotiyu.com") > -1;
      window.location.href = isProd ? "https://sport.yanhuotiyu.com" : "http://localhost:8080";
    },

    toLoginPage() {
      localStorage.removeItem("user");
      this.$router.replace("/login");
    }
  }
};
</script>

<style scoped>
.sso-wrap {
  position: relative;
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 18% 12%, #2b1a5e 0%, #150f2b 45%, #0b0a17 100%);
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
}

/* 背景装饰 */
.sso-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.5;
  pointer-events: none;
}

.sso-glow-a {
  width: 420px;
  height: 420px;
  top: -140px;
  left: -110px;
  background: #7c3aed;
}

.sso-glow-b {
  width: 360px;
  height: 360px;
  right: -110px;
  bottom: -110px;
  background: #2563eb;
}

.sso-grid {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
  linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: radial-gradient(circle at 50% 45%, #000 0%, transparent 72%);
  -webkit-mask-image: radial-gradient(circle at 50% 45%, #000 0%, transparent 72%);
  pointer-events: none;
}

.sso-card {
  position: relative;
  z-index: 2;
  width: 420px;
  max-width: calc(100vw - 40px);
  box-sizing: border-box;
  padding: 26px 28px 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  animation: sso-in 0.45s cubic-bezier(0.22, 0.68, 0.36, 1);
}

@keyframes sso-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 品牌 */
.sso-brand {
  display: flex;
  align-items: center;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sso-logo {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: #fff;
  font-size: 21px;
  font-weight: 700;
  line-height: 42px;
  text-align: center;
  box-shadow: 0 6px 18px rgba(124, 58, 237, 0.5);
}

.sso-brand-text {
  margin-left: 12px;
  flex: 1;
  min-width: 0;
}

.sso-brand-name {
  font-size: 15px;
  font-weight: 600;
  color: #f3f4f6;
  letter-spacing: 0.5px;
}

.sso-brand-sub {
  margin-top: 3px;
  font-size: 12px;
  color: #a5a3bd;
}

.sso-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.18);
  border: 1px solid rgba(139, 92, 246, 0.35);
}

/* 状态区 */
.sso-body {
  padding: 30px 4px 8px;
  text-align: center;
}

.sso-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 14px;
}

.sso-svg {
  width: 100%;
  height: 100%;
}

.sso-icon.is-ok {
  color: #34d399;
  animation: sso-pop 0.4s cubic-bezier(0.3, 1.4, 0.5, 1);
}

.sso-icon.is-err {
  color: #f87171;
  animation: sso-pop 0.4s cubic-bezier(0.3, 1.4, 0.5, 1);
}

@keyframes sso-pop {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.sso-title {
  font-size: 16px;
  font-weight: 600;
  color: #f9fafb;
  letter-spacing: 0.4px;
}

.sso-desc {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.7;
  color: #9ca3af;
}

.sso-desc.is-err-text {
  color: #fca5a5;
}

/* 加载动画 */
.sso-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 54px;
  margin-bottom: 14px;
}

.sso-spinner span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  animation: sso-bounce 1.1s ease-in-out infinite;
}

.sso-spinner span:nth-child(2) {
  animation-delay: 0.15s;
}

.sso-spinner span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes sso-bounce {
  0%, 80%, 100% {
    transform: translateY(0) scale(0.85);
    opacity: 0.6;
  }
  40% {
    transform: translateY(-9px) scale(1);
    opacity: 1;
  }
}

/* 进度条 */
.sso-progress {
  height: 3px;
  margin: 18px 10px 0;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.sso-progress i {
  display: block;
  height: 100%;
  width: 20%;
  border-radius: 3px;
  background: linear-gradient(90deg, #8b5cf6, #22d3ee);
  transition: width 0.5s ease;
}

/* 步骤条 */
.sso-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.sso-step {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #6b7280;
  transition: color 0.3s;
}

.sso-step i {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-style: normal;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
  color: #9ca3af;
  background: rgba(255, 255, 255, 0.08);
  transition: all 0.3s;
}

.sso-step.is-done {
  color: #c4b5fd;
}

.sso-step.is-done i {
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
}

.sso-step-line {
  width: 26px;
  height: 1px;
  margin: 0 8px;
  background: rgba(255, 255, 255, 0.12);
}

/* 按钮 */
.sso-actions {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sso-btn {
  width: 100%;
  padding: 11px 0;
  font-size: 14px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
  color: #e5e7eb;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;
}

.sso-btn:hover {
  background: rgba(255, 255, 255, 0.12);
}

.sso-btn.is-primary {
  border: none;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #4f46e5);
  box-shadow: 0 8px 22px rgba(79, 70, 229, 0.42);
}

.sso-btn.is-primary:hover {
  filter: brightness(1.08);
}

/* 页脚 */
.sso-foot {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 11px;
  color: #6b7280;
  letter-spacing: 0.4px;
  text-align: center;
}

.sso-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 6px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 8px #34d399;
  vertical-align: middle;
}
</style>
